import React, { useEffect, useRef, useState } from 'react'
import {
  Paper, Button, IconButton, Switch, FormControlLabel,
  Snackbar, Dialog, DialogTitle, DialogContent, DialogActions,
  CircularProgress, useTheme, TextField, Tooltip
} from '@mui/material'
import AddIcon from '@mui/icons-material/Add'
import EditIcon from '@mui/icons-material/Edit'
import DeleteIcon from '@mui/icons-material/Delete'
import CampaignIcon from '@mui/icons-material/Campaign'
import MouseIcon from '@mui/icons-material/Mouse'
import hipersendService from '../../services/hipersend'
import { safeHtmlProps } from '../../libs/sanitizeHtml'
import Text from '../Text'
import TextFieldCustom from '../TextFieldCustom'

const EMPTY_BANNER = {
  title: '',
  htmlContent: '',
  position: 'top',
  isActive: true,
  startDate: '',
  endDate: '',
  order: 0
}

const SNIPPETS = {
  imagem: '<img src="https://exemplo.com/imagem.jpg" alt="" style="width: 100%; border-radius: 8px;" />',
  link: '<a href="https://exemplo.com" target="_blank" style="color: #03adec; font-weight: bold;">Clique aqui</a>',
  tabela: '<table style="width: 100%; border-collapse: collapse;">\n  <tr style="background: #f0f0f0;">\n    <th style="padding: 8px; border: 1px solid #ddd;">Coluna A</th>\n    <th style="padding: 8px; border: 1px solid #ddd;">Coluna B</th>\n  </tr>\n  <tr>\n    <td style="padding: 8px; border: 1px solid #ddd;">Valor 1</td>\n    <td style="padding: 8px; border: 1px solid #ddd;">Valor 2</td>\n  </tr>\n</table>',
  heading: '<h2 style="color: #333; font-size: 20px; margin: 0;">Titulo em destaque</h2>',
  grid: '<div style="display: grid; grid-template-columns: 1fr 1fr; gap: 12px;">\n  <div style="padding: 16px; background: #f5f5f5; border-radius: 8px;">\n    <strong>Card 1</strong>\n    <p>Conteudo do card</p>\n  </div>\n  <div style="padding: 16px; background: #f5f5f5; border-radius: 8px;">\n    <strong>Card 2</strong>\n    <p>Conteudo do card</p>\n  </div>\n</div>',
  cardPromo: '<div style="background: linear-gradient(135deg, #667eea 0%, #764ba2 100%); color: white; padding: 20px; border-radius: 12px; text-align: center;">\n  <h2 style="margin: 0 0 8px 0; font-size: 22px;">OFERTA ESPECIAL</h2>\n  <p style="margin: 0 0 12px 0;">Aproveite agora mesmo</p>\n  <a href="https://exemplo.com" target="_blank" style="display: inline-block; background: white; color: #667eea; padding: 10px 24px; border-radius: 6px; text-decoration: none; font-weight: bold;">Saiba mais</a>\n</div>'
}

export default function BannerManager({ clientId, clientSecret }) {
  const theme = useTheme()
  const textareaRef = useRef(null)

  const [banners, setBanners] = useState([])
  const [loading, setLoading] = useState(false)

  const [formOpen, setFormOpen] = useState(false)
  const [editingBanner, setEditingBanner] = useState(null)
  const [form, setForm] = useState(EMPTY_BANNER)
  const [saving, setSaving] = useState(false)

  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false)
  const [bannerToDelete, setBannerToDelete] = useState(null)

  const [message, setMessage] = useState('')

  // Modelos prontos vindos do HiperPay. Falha na busca só esconde o atalho —
  // criar banner do zero continua funcionando.
  const [modelos, setModelos] = useState([])

  useEffect(() => {
    if (clientId && clientSecret) {
      fetchBanners()
      hipersendService.hiperpay
        .getBannerTemplates(clientId, clientSecret)
        .then(res => setModelos(res?.modelos || []))
        .catch(() => setModelos([]))
    }
  }, [clientId, clientSecret])

  const fetchBanners = async () => {
    setLoading(true)
    try {
      const data = await hipersendService.hiperpay.getBanners(clientId, clientSecret)
      setBanners(data)
    } catch (error) {
      console.error(error)
    } finally {
      setLoading(false)
    }
  }

  const openCreateForm = () => {
    setEditingBanner(null)
    setForm(EMPTY_BANNER)
    setFormOpen(true)
  }

  // Modelo entra como rascunho: o provedor ajusta texto e datas antes de salvar,
  // então nada é criado sem ele ver.
  const openFormFromTemplate = (modelo) => {
    setEditingBanner(null)
    setForm({
      ...EMPTY_BANNER,
      title: modelo.title,
      htmlContent: modelo.htmlContent,
      position: modelo.position
    })
    setFormOpen(true)
  }

  const openEditForm = (banner) => {
    setEditingBanner(banner)
    setForm({
      title: banner.title || '',
      htmlContent: banner.htmlContent || '',
      position: banner.position || 'top',
      isActive: banner.isActive !== undefined ? banner.isActive : true,
      startDate: banner.startDate ? banner.startDate.slice(0, 16) : '',
      endDate: banner.endDate ? banner.endDate.slice(0, 16) : '',
      order: banner.order || 0
    })
    setFormOpen(true)
  }

  const handleSave = async () => {
    if (!form.title.trim()) {
      setMessage('Titulo e obrigatorio (so para identificar no painel)')
      return
    }
    if (!form.htmlContent.trim()) {
      setMessage('Conteudo HTML e obrigatorio')
      return
    }
    setSaving(true)
    try {
      const payload = {
        ...form,
        startDate: form.startDate || null,
        endDate: form.endDate || null
      }
      if (editingBanner) {
        await hipersendService.hiperpay.updateBanner(clientId, clientSecret, editingBanner._id, payload)
        setMessage('Banner atualizado')
      } else {
        await hipersendService.hiperpay.createBanner(clientId, clientSecret, payload)
        setMessage('Banner criado')
      }
      setFormOpen(false)
      await fetchBanners()
    } catch (error) {
      console.error(error)
      const details = error.response?.data?.details
      setMessage(details ? details.join(', ') : 'Erro ao salvar banner')
    } finally {
      setSaving(false)
    }
  }

  const handleToggleActive = async (banner) => {
    try {
      await hipersendService.hiperpay.updateBanner(clientId, clientSecret, banner._id, {
        title: banner.title,
        htmlContent: banner.htmlContent,
        position: banner.position,
        order: banner.order,
        isActive: !banner.isActive,
        startDate: banner.startDate || null,
        endDate: banner.endDate || null
      })
      setMessage(banner.isActive ? 'Banner desativado' : 'Banner ativado')
      await fetchBanners()
    } catch (error) {
      console.error(error)
      setMessage('Erro ao atualizar banner')
    }
  }

  const handleDelete = async () => {
    if (!bannerToDelete) return
    try {
      await hipersendService.hiperpay.deleteBanner(clientId, clientSecret, bannerToDelete._id)
      setMessage('Banner removido')
      setDeleteDialogOpen(false)
      setBannerToDelete(null)
      await fetchBanners()
    } catch (error) {
      console.error(error)
      setMessage('Erro ao remover banner')
    }
  }

  const updateField = (field) => (e) => {
    setForm(prev => ({ ...prev, [field]: e.target.value }))
  }

  const insertSnippet = (snippet) => {
    const textarea = textareaRef.current
    if (!textarea) {
      setForm(prev => ({ ...prev, htmlContent: prev.htmlContent + '\n' + snippet }))
      return
    }
    const start = textarea.selectionStart
    const end = textarea.selectionEnd
    const current = form.htmlContent
    const updated = current.substring(0, start) + snippet + current.substring(end)
    setForm(prev => ({ ...prev, htmlContent: updated }))
    setTimeout(() => {
      textarea.focus()
      textarea.setSelectionRange(start + snippet.length, start + snippet.length)
    }, 0)
  }

  const formatDateLabel = (dateStr) => {
    if (!dateStr) return null
    const d = new Date(dateStr)
    return d.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' })
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-2">
          <CampaignIcon style={{ color: theme.palette.primary.main }} />
          <Text className="font-medium text-lg">Banners e Promocoes ({banners.length})</Text>
        </div>
        <Button
          variant="outlined"
          startIcon={<AddIcon />}
          onClick={openCreateForm}
          size="small"
        >
          Novo Banner
        </Button>
      </div>

      {modelos.length > 0 && (
        <div className="flex flex-wrap items-center gap-2">
          <Text className="text-xs font-bold opacity-50 mr-1">COMECE POR UM MODELO:</Text>
          {modelos.map(modelo => (
            <Tooltip key={modelo.chave} title={modelo.descricao}>
              <Button
                variant="outlined"
                size="small"
                onClick={() => openFormFromTemplate(modelo)}
              >
                {modelo.nome}
              </Button>
            </Tooltip>
          ))}
        </div>
      )}

      <Text className="text-sm opacity-60">
        Banners aparecem dinamicamente em todas as paginas de pagamento desta integracao. Suporta HTML completo (tabelas, grids, imagens, links) com sanitizacao automatica. Cliques sao rastreados para estatistica.
      </Text>

      {loading ? (
        <div className="flex justify-center py-4">
          <CircularProgress size={24} />
        </div>
      ) : banners.length === 0 ? (
        <Paper elevation={0} className="p-6 text-center" style={{ backgroundColor: theme.isDark ? 'rgba(255,255,255,0.03)' : '#fafafa' }}>
          <Text className="opacity-40">Nenhum banner cadastrado</Text>
        </Paper>
      ) : (
        <div className="space-y-2">
          {banners.map((banner) => (
            <Paper key={banner._id} elevation={1} className="p-3">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-3">
                  <div
                    className="w-10 h-10 rounded flex-shrink-0 flex items-center justify-center text-xs font-bold"
                    style={{ backgroundColor: theme.isDark ? 'rgba(255,255,255,0.05)' : '#f5f5f5', border: '1px solid #ddd' }}
                  >
                    {banner.position === 'top' ? 'T' : 'B'}
                  </div>
                  <div>
                    <div className="flex items-center space-x-2">
                      <Text className="font-medium">{banner.title}</Text>
                      {!banner.isActive && (
                        <span className="text-xs px-1.5 py-0.5 rounded bg-gray-400 text-white">INATIVO</span>
                      )}
                      <Tooltip title="Total de cliques registrados">
                        <div className="flex items-center space-x-1 text-xs opacity-60">
                          <MouseIcon style={{ fontSize: 14 }} />
                          <span>{banner.clickCount || 0}</span>
                        </div>
                      </Tooltip>
                    </div>
                    <div className="flex items-center space-x-3">
                      {banner.startDate && (
                        <Text className="text-xs opacity-50">Inicio: {formatDateLabel(banner.startDate)}</Text>
                      )}
                      {banner.endDate && (
                        <Text className="text-xs opacity-50">Fim: {formatDateLabel(banner.endDate)}</Text>
                      )}
                    </div>
                  </div>
                </div>
                <div className="flex items-center space-x-1">
                  <Switch
                    size="small"
                    checked={banner.isActive}
                    onChange={() => handleToggleActive(banner)}
                    color="primary"
                  />
                  <IconButton size="small" onClick={() => openEditForm(banner)}>
                    <EditIcon fontSize="small" />
                  </IconButton>
                  <IconButton size="small" onClick={() => { setBannerToDelete(banner); setDeleteDialogOpen(true) }}>
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </div>
              </div>
            </Paper>
          ))}
        </div>
      )}

      {/* Form Dialog */}
      <Dialog open={formOpen} onClose={() => setFormOpen(false)} maxWidth="lg" fullWidth>
        <DialogTitle>{editingBanner ? 'Editar Banner' : 'Novo Banner'}</DialogTitle>
        <DialogContent>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pt-2">
            {/* Coluna esquerda: Editor */}
            <div className="space-y-3">
              <TextFieldCustom label="Titulo *" value={form.title} onChange={updateField('title')} helperText="So para identificar o banner no painel" />

              <div>
                <Text className="text-sm font-medium mb-2">Insercao rapida:</Text>
                <div className="flex flex-wrap gap-1">
                  <Button size="small" variant="outlined" onClick={() => insertSnippet(SNIPPETS.imagem)}>Imagem</Button>
                  <Button size="small" variant="outlined" onClick={() => insertSnippet(SNIPPETS.link)}>Link</Button>
                  <Button size="small" variant="outlined" onClick={() => insertSnippet(SNIPPETS.tabela)}>Tabela</Button>
                  <Button size="small" variant="outlined" onClick={() => insertSnippet(SNIPPETS.heading)}>Titulo</Button>
                  <Button size="small" variant="outlined" onClick={() => insertSnippet(SNIPPETS.grid)}>Grid</Button>
                  <Button size="small" variant="outlined" onClick={() => insertSnippet(SNIPPETS.cardPromo)}>Card Promo</Button>
                </div>
              </div>

              <TextField
                fullWidth
                label="Conteudo HTML *"
                multiline
                rows={14}
                value={form.htmlContent}
                onChange={updateField('htmlContent')}
                inputRef={textareaRef}
                placeholder='<div style="padding: 16px; background: #f5f5f5;">Seu conteudo aqui...</div>'
                InputProps={{
                  style: { fontFamily: 'monospace', fontSize: '12px' }
                }}
                helperText="Use HTML livre. Tags perigosas (script, iframe) sao removidas automaticamente."
              />

              <TextFieldCustom
                label="Posicao"
                value={form.position}
                onChange={updateField('position')}
                select
                SelectProps={{ native: true }}
              >
                <option value="top">Topo (acima do pagamento)</option>
                <option value="bottom">Rodape (abaixo do pagamento)</option>
              </TextFieldCustom>
              <TextField
                fullWidth
                size="small"
                label="Data de inicio (opcional)"
                type="datetime-local"
                value={form.startDate}
                onChange={updateField('startDate')}
                InputLabelProps={{ shrink: true }}
              />
              <TextField
                fullWidth
                size="small"
                label="Data de termino (opcional)"
                type="datetime-local"
                value={form.endDate}
                onChange={updateField('endDate')}
                InputLabelProps={{ shrink: true }}
                helperText="Banners com data de termino exibem contador regressivo automatico"
              />
              <TextFieldCustom
                label="Ordem de exibicao"
                type="number"
                value={form.order}
                onChange={(e) => setForm(prev => ({ ...prev, order: parseInt(e.target.value) || 0 }))}
              />
              <FormControlLabel
                control={
                  <Switch
                    checked={form.isActive}
                    onChange={(e) => setForm(prev => ({ ...prev, isActive: e.target.checked }))}
                    color="primary"
                  />
                }
                label="Ativo"
              />
            </div>

            {/* Coluna direita: Preview */}
            <div>
              <Text className="text-sm font-medium mb-2">Preview ao vivo</Text>
              <Paper
                elevation={0}
                className="p-4 rounded"
                style={{ backgroundColor: theme.isDark ? 'rgba(255,255,255,0.03)' : '#f0f0f0', minHeight: '400px' }}
              >
                <div className="max-w-[500px] mx-auto bg-white rounded p-2" style={{ color: '#333' }}>
                  {form.htmlContent ? (
                    <div {...safeHtmlProps(form.htmlContent)} />
                  ) : (
                    <div className="text-center opacity-40 py-20">
                      <Text>O preview aparecera aqui conforme voce digita</Text>
                    </div>
                  )}
                </div>
                <Text className="text-xs opacity-50 text-center mt-3">
                  Preview sanitizado (scripts e handlers removidos)
                </Text>
              </Paper>
            </div>
          </div>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setFormOpen(false)}>Cancelar</Button>
          <Button variant="contained" onClick={handleSave} disabled={saving}>
            {saving ? <CircularProgress size={20} /> : 'Salvar'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation */}
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle>Remover banner</DialogTitle>
        <DialogContent>
          <Text>
            Tem certeza que deseja remover o banner "{bannerToDelete?.title}"?
            Ele sera removido de todas as paginas de pagamento imediatamente.
          </Text>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>Cancelar</Button>
          <Button variant="contained" color="error" onClick={handleDelete}>Remover</Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={!!message}
        autoHideDuration={4000}
        onClose={() => setMessage('')}
        message={message}
      />
    </div>
  )
}
